import { useState, useCallback, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { IMAGES } from "../i18n/content";
import { Reveal } from "../components/Reveal";
import { PageIntro } from "../components/PageIntro";
import { FinalCTA } from "../components/FinalCTA";

// Bento span pattern for asymmetric grid
const SPANS = [
  "md:col-span-7 md:row-span-2",
  "md:col-span-5",
  "md:col-span-5",
  "md:col-span-4",
  "md:col-span-4",
  "md:col-span-4",
];

export default function Gallery() {
  const { t } = useLang();
  const images = IMAGES.gallery;
  const [index, setIndex] = useState(null);
  const open = index !== null;

  const close = useCallback(() => setIndex(null), []);
  const next = useCallback(() => setIndex((i) => (i + 1) % images.length), [images.length]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + images.length) % images.length), [images.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, close, next, prev]);

  return (
    <>
      <PageIntro overline={t("galleryPage.overline")} title={t("galleryPage.title")} body={t("galleryPage.body")} testId="gallery-intro" />

      <section className="bg-cream pb-24 lg:pb-32" data-testid="gallery-grid">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid auto-rows-[220px] grid-cols-1 gap-4 md:grid-cols-12 md:auto-rows-[260px]">
            {images.map((img, i) => (
              <Reveal key={img.src} delay={(i % 3) * 0.08} className={SPANS[i % SPANS.length]}>
                <button
                  type="button"
                  onClick={() => setIndex(i)}
                  data-testid={`gallery-item-${i}`}
                  className="group relative h-full w-full overflow-hidden rounded-xl bg-sand"
                  aria-label={`Open image ${i + 1}`}
                >
                  <img
                    src={img.src}
                    alt={`Shakti Love Music session ${i + 1}`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <span className="absolute inset-0 bg-charcoal/0 transition-colors duration-500 group-hover:bg-charcoal/15" />
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-charcoal/90 p-4 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            aria-label="Image viewer"
            data-testid="gallery-lightbox"
            onClick={close}
          >
            <button onClick={close} data-testid="lightbox-close" aria-label={t("galleryPage.close")} className="absolute right-5 top-5 flex h-12 w-12 items-center justify-center rounded-full bg-cream/10 text-cream transition-colors hover:bg-cream/20">
              <X className="h-6 w-6" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); prev(); }} data-testid="lightbox-prev" aria-label="Previous" className="absolute left-4 flex h-12 w-12 items-center justify-center rounded-full bg-cream/10 text-cream transition-colors hover:bg-cream/20 md:left-8">
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); next(); }} data-testid="lightbox-next" aria-label="Next" className="absolute right-4 flex h-12 w-12 items-center justify-center rounded-full bg-cream/10 text-cream transition-colors hover:bg-cream/20 md:right-8">
              <ChevronRight className="h-6 w-6" />
            </button>
            <motion.img
              key={index}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              src={images[index].src}
              alt={`Shakti Love Music session ${index + 1}`}
              className="max-h-[85vh] max-w-[90vw] rounded-lg object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <FinalCTA />
    </>
  );
}
